import { createContext, useContext, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { ApiError, apiFetch, post, put, del } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";
import type { UnifiedEntityConfigForm } from "@/hooks/use-unified-config";

// ── Types ──────────────────────────────────────────────────────────────────────

export type SyncDirection = "inbound" | "outbound" | "bidirectional";

export type FormFieldType =
  | "text" | "textarea" | "number" | "email" | "url" | "password" | "color"
  | "select" | "multiselect" | "radio"
  | "checkbox"
  | "date" | "time" | "datetime-local"
  | "range"
  | "section";

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  placeholder?: string;
  description?: string;
  required?: boolean;
  defaultValue?: string | number | boolean | string[];
  options?: Array<string | FormFieldOption>;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  /** Dynamic options via connector action. When set on a select/multiselect,
   *  options are fetched live instead of using the static options array. */
  enrich?: import("@/hooks/use-configs").EnrichSpec;
  /** Field keys this field depends on. The field is disabled until all
   *  dependencies have values, and clears when any dependency changes. */
  dependsOn?: string[];
}

export interface WidgetFormSchema {
  title?: string;
  description?: string;
  submitLabel?: string;
  fields: FormField[];
}

export type ActivationMode = "SINGLE_ACTIVATION" | "MULTI_CONNECTION";

export interface WidgetConnectorRef {
  connectorId: string;
  slug: string;
  // Optional widget-level overrides; unset means "use the connector's catalog
  // default". Scope (SaaS vs multi-tenant) and SaaS connection pin DO NOT
  // live here — they're workflow.connector_manifest concerns.
  authMethodId?: string;
  authProviderId?: string;
  deleteOnDeactivate?: boolean;
  // Presentation only: omit this connector's logo from the hub logo stack.
  // The connector itself stays in the widget — auth, connect and execution
  // are untouched.
  hideLogo?: boolean;
  // SaaS-connector connection scope (e.g. "inventory"). Only settable for
  // connectors registered under Settings > SaaS Connectors. At activation the
  // server pins the tenant's connection with this scope; the customer never
  // sees this connector in the connect flow. Not the manifest tenancy scope.
  connectionScope?: string | null;
  // Read-only, set by the API: this connector is the widget owner's registered
  // product (saas_connectors). The hub never shows it on the card; a credentials
  // product is connected by the partner's backend, an OAuth one in the connect
  // step. Never sent back on a write.
  product?: boolean;
}

/** The widget's per-connector overrides for one connector, as set in the
 *  builder's connector settings popup. Matches on connectorId first, then slug —
 *  catalog inheritance can surface the same logical app under a different
 *  connector_id at the partner vs the tenant scope, and the ref must still
 *  resolve so the developer's auth-provider pick is honoured at connect time. */
export function widgetConnectorRef(
  widget: { connectorRefs?: WidgetConnectorRef[] } | null | undefined,
  connector: { id: string; slug?: string } | null | undefined,
): WidgetConnectorRef | undefined {
  const refs = widget?.connectorRefs;
  if (!refs || refs.length === 0 || !connector) return undefined;
  return (
    refs.find((r) => r.connectorId === connector.id) ??
    (connector.slug ? refs.find((r) => r.slug === connector.slug) : undefined)
  );
}

export interface WidgetLifecycleHookRef {
  workflowId: string;
  version?: string;
  // Explicit on/off. Absent === enabled. Lets the developer mute a hook
  // without losing the workflow pin.
  enabled?: boolean;
  // "blocking" (default when absent) blocks the action until the workflow
  // accepts; "background" runs in the background and cannot veto.
  mode?: "blocking" | "background";
  // Also run when the customer acts in their own session (embed, share link).
  runForCustomer?: boolean;
}

export interface WidgetLifecycleHooks {
  onActivation?: WidgetLifecycleHookRef | null;
  onDeactivation?: WidgetLifecycleHookRef | null;
  onConfigurationCreated?: WidgetLifecycleHookRef | null;
  onConfigurationChange?: WidgetLifecycleHookRef | null;
  onConfigurationDelete?: WidgetLifecycleHookRef | null;
  // Trigger lifecycle callbacks: per action the builder picks ONE of the
  // before/on pair (before = blocking can veto, create/update can also
  // rewrite settings; after = receives the trigger data). The installation
  // delete cascade only fires onTriggerDeleted.
  beforeTriggerCreated?: WidgetLifecycleHookRef | null;
  onTriggerCreated?: WidgetLifecycleHookRef | null;
  beforeTriggerUpdated?: WidgetLifecycleHookRef | null;
  onTriggerUpdated?: WidgetLifecycleHookRef | null;
  beforeTriggerDeleted?: WidgetLifecycleHookRef | null;
  onTriggerDeleted?: WidgetLifecycleHookRef | null;
}

export type WidgetTriggerType = "WEBHOOK" | "SCHEDULE" | "APP_EVENT";

export interface WidgetTriggerRatePreset {
  value: number;
  unit: "minutes" | "hours" | "days";
}

export interface WidgetTriggerCronPreset {
  label: string;
  expression: string;
}

export interface WidgetTriggerScheduleOptions {
  ratePresets?: WidgetTriggerRatePreset[];
  cronPresets?: WidgetTriggerCronPreset[];
  // Allow the end user to type a custom rate/cron instead of picking a preset.
  allowCustom?: boolean;
}

// Template-trigger configuration lives on the widget's trigger_refs — never on
// the trigger rows themselves. Mirrors TriggerRefSchema in services/api.
export interface WidgetTriggerRef {
  triggerId: string;
  type: WidgetTriggerType;
  endUserEditable: boolean;
  // SCHEDULE only: subset of ["cron", "rate", "timezone", "startsAt"].
  editableFields: string[];
  // e.g. { rate: { value, unit } } or { cron: { expression } }, plus timezone.
  defaultSettings: Record<string, unknown>;
  label?: string;
  scheduleOptions?: WidgetTriggerScheduleOptions;
  // Builder-defined headers merged into every created route binding. End-user
  // extras (when allowExtraHeaders) merge over these; x-installation-id and
  // x-end-org-id are always stamped last and can never be overridden.
  defaultHeaders?: Record<string, string>;
  allowExtraHeaders?: boolean;
  // widget.triggerCategories slugs this template belongs to. A template
  // provisions when ANY of its categories is selected at install time.
  // Absent/empty = core: always provisioned regardless of selection.
  categories?: string[];
  // APP_EVENT only. USER (absent) = runs on the installation's connection.
  // NONE = no connection; the webhook names the end org itself.
  activation?: WidgetTriggerActivation;
}

export type WidgetTriggerActivation = "USER" | "NONE";

// Named trigger groups (e.g. sync directions) the installer picks from at
// activation. Mirrors TriggerCategorySchema in services/api.
export interface WidgetTriggerCategory {
  slug: string;
  label: string;
  description?: string;
}

export interface Widget {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  description: string | null;
  connectorIds: string[];
  workflowIds: string[];
  /** Customer-facing catalogue only: what the integration sets up, by name,
   *  for a customer that has not connected it yet (no code, nothing runnable). */
  workflowSummaries?: { id: string; name: string; description: string | null }[];
  triggerIds: string[];
  agentContext: string | null;
  syncDirection: SyncDirection;
  styleOverrides: Record<string, string> | null;
  formSchema: WidgetFormSchema | null;
  status: string;
  // Typed refs / lifecycle / visibility (added by migration 093). Backwards-compat:
  // backend dual-writes both bare-ID arrays and typed refs, so old code reading
  // connectorIds keeps working while new code can read connectorRefs.
  connectorRefs?: WidgetConnectorRef[];
  // Per-trigger template settings (end-user editability, presets, defaults).
  // Authoritative over triggerIds when non-empty.
  triggerRefs?: WidgetTriggerRef[];
  // Trigger group catalog the install wizard offers for selection.
  triggerCategories?: WidgetTriggerCategory[];
  // Optional heading for the wizard's sync-type selection step; null falls
  // back to the built-in "What do you want to sync?".
  triggerCategoriesTitle?: string | null;
  // Unified catalog entities attached to this widget. Slugs/names are
  // resolved server-side from the platform catalog.
  unifiedRefs?: WidgetUnifiedRef[];
  // Derived server-side on every write: UNIFIED when unified entities are
  // attached, APP otherwise.
  type?: "APP" | "UNIFIED";
  activationMode?: ActivationMode;
  enabled?: boolean;
  allowedEndOrgIds?: string[] | null;
  lifecycleHooks?: WidgetLifecycleHooks;
  /** Set on a customised copy: the widget it was forked from. */
  forkedFromWidgetId?: string | null;
  /** The owner's own How it works text, which replaces the generated doc. */
  documentationOverride?: { markdown: string; editedAt: string | null } | null;
  /** The owner showed the How it works doc to its customers (end users). */
  docsVisibleToEndUsers?: boolean;
  /** Customer catalog only: this row is one version of an integration the
   *  customer customised. Both rows of a pair share `groupId` (the original's
   *  id) and exactly one is `active`. */
  version?: WidgetVersion;
  /** A child org's own team only: this is its direct parent's integration. The
   *  team can run it; it cannot edit it and does not offer it to its customers. */
  inheritedFrom?: { orgId: string; orgName: string | null };
  /** This org's customised copy of its parent's integration. */
  customisedFrom?: { sourceWidgetId: string; sourceOrgName: string | null; sourceName: string | null };
  /** Partner's own list: how many customers run a customised version of this widget. */
  customisedByCount?: number;
  /** Partner's own list: how many customers have a live setup on this widget. */
  installedByCount?: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WidgetVersion {
  groupId: string;
  kind: "custom" | "original";
  active: boolean;
  /** False when the provider deleted, disabled or stopped offering the original:
   *  the customer's version keeps running and is the only one. */
  originalAvailable: boolean;
  /** The original is gone but the customer's setups may still be on it:
   *  switching to its own version moves them. */
  stranded?: boolean;
}

export interface WidgetUnifiedRef {
  entityId: string;
  categorySlug: string;
  entitySlug: string;
  name: string;
  /** Providers chosen for this entity; resolved server-side, never empty. */
  connectorSlugs?: string[];
  /** Owner-defined per-provider configuration form. Keys are connector slugs
   *  within connectorSlugs. Omitted on PUT preserves the stored form. */
  configForm?: UnifiedEntityConfigForm;
}

/** True when the widget's unified entities define at least one config-form
 *  field. Unified widgets carry no config template until one is saved, so this
 *  — not the presence of a config row — is what decides whether a
 *  configuration surface has anything to show. */
export function widgetHasUnifiedForms(
  refs: WidgetUnifiedRef[] | null | undefined,
): boolean {
  return (refs ?? []).some(
    (r) => r.configForm && Object.values(r.configForm).some((fields) => fields.length > 0),
  );
}

export interface CreateWidgetInput {
  name: string;
  description?: string;
  connector_ids: string[];
  workflow_ids?: string[];
  trigger_ids?: string[];
  trigger_refs?: WidgetTriggerRef[];
  trigger_categories?: WidgetTriggerCategory[];
  trigger_categories_title?: string | null;
  unified_refs?: Array<{ entityId: string; connectorSlugs?: string[]; configForm?: UnifiedEntityConfigForm }>;
  agent_context?: string;
  sync_direction?: SyncDirection;
  style_overrides?: Record<string, string> | null;
  form_schema?: WidgetFormSchema | null;
  status?: "active" | "draft" | "archived";
}

export interface UpdateWidgetInput {
  name?: string;
  description?: string | null;
  connector_ids?: string[];
  connector_refs?: WidgetConnectorRef[];
  workflow_ids?: string[];
  trigger_ids?: string[];
  trigger_refs?: WidgetTriggerRef[];
  trigger_categories?: WidgetTriggerCategory[];
  trigger_categories_title?: string | null;
  unified_refs?: Array<{ entityId: string; connectorSlugs?: string[]; configForm?: UnifiedEntityConfigForm }>;
  agent_context?: string | null;
  sync_direction?: SyncDirection;
  style_overrides?: Record<string, string> | null;
  form_schema?: WidgetFormSchema | null;
  status?: "active" | "draft" | "archived";
  activation_mode?: ActivationMode;
  enabled?: boolean;
  allowed_end_org_ids?: string[] | null;
  lifecycle_hooks?: WidgetLifecycleHooks;
}

// ── Queries ────────────────────────────────────────────────────────────────────

export function useWidgets(endOrgId?: string, opts?: {
  enabled?: boolean;
  /** Only widgets this org built or customised: drops the parent's integrations
   *  a child org's team is shown (inheritedFrom), which it can run but not edit
   *  and does not offer to its own customers. */
  ownOnly?: boolean;
}) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  // When previewing a tenant ("view as <tenant>"), scope the catalog to that
  // tenant so the customer-view preview shows the tenant's OWN widgets plus
  // inherited account widgets — matching the embed. Cached under a distinct
  // key so it never clobbers the account-scoped management list.
  return useQuery({
    queryKey: keys.widgets.list(endOrgId ?? orgId),
    // include=inherited: a child org's team is shown its parent's integrations
    // (inheritedFrom). Other roles get them by default; platform_admin only
    // when asked, so the admin console's view of an org is unchanged.
    queryFn: () => apiFetch<Widget[]>(`${API_BASE}/v1/widgets?${endOrgId ? `endOrgId=${encodeURIComponent(endOrgId)}` : "include=inherited"}`),
    enabled: !!orgId && (opts?.enabled ?? true),
    ...(opts?.ownOnly ? { select: (rows: Widget[]) => rows.filter((w) => !w.inheritedFrom) } : {}),
  });
}

/**
 * How many connectors one widget may list for the org (GET /api/v1/me).
 * `limit` null = unlimited, also while loading or when it could not be read:
 * the API enforces it either way, so the builder never blocks on a guess.
 */
export function useWidgetConnectorLimit(): { limit: number | null } {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const q = useQuery({
    queryKey: keys.widgets.connectorLimit(orgId),
    queryFn: () => apiFetch<{ plan: { id: string; limits: { connectorsPerWidget: number | null } } | null }>(`${API_BASE}/v1/me`),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
  return { limit: q.data?.plan?.limits.connectorsPerWidget ?? null };
}

export function useWidget(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.widgets.detail(orgId, id),
    queryFn: () => apiFetch<Widget>(`${API_BASE}/v1/widgets/${encodeURIComponent(id!)}`),
    enabled: !!orgId && !!id,
  });
}

/** An integration's How it works doc (GET /api/v1/widgets/:id/docs). */
export interface WidgetDocs {
  widgetId: string;
  /** The owner's own text, else the generated doc, else null. */
  markdown: string | null;
  source: "override" | "generated" | null;
  generatedAt: string | null;
  stale: boolean;
  /** A generation is running: poll until it is false. */
  pending: boolean;
  /** Whether the owner shows the doc to its customers. */
  visibleToEndUsers: boolean;
}

/** How long a view keeps polling a doc that is being generated. */
export const WIDGET_DOCS_POLL_MS = 5_000;
export const WIDGET_DOCS_POLL_FOR_MS = 3 * 60_000;

/**
 * The How it works doc of one integration. Keyed on the widget id, so a card
 * whose active version changes reads the newly active version's doc. Polls
 * while a generation is pending, for a few minutes at most.
 * `endOrgId` is the hub's preview tenant, sent exactly as useWidgets sends it.
 */
export function useWidgetDocs(widgetId: string | undefined, endOrgId?: string, opts?: { enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const [startedAt] = useState(() => Date.now());
  return useQuery({
    queryKey: keys.widgets.docs(orgId, widgetId, endOrgId),
    queryFn: () => apiFetch<WidgetDocs>(
      `${API_BASE}/v1/widgets/${encodeURIComponent(widgetId!)}/docs?${endOrgId ? `endOrgId=${encodeURIComponent(endOrgId)}` : "include=inherited"}`,
    ),
    enabled: !!orgId && !!widgetId && (opts?.enabled ?? true),
    refetchInterval: (query) =>
      query.state.data?.pending && Date.now() - startedAt < WIDGET_DOCS_POLL_FOR_MS ? WIDGET_DOCS_POLL_MS : false,
  });
}

/** The one write path for the doc (PUT /api/v1/widgets/:id/docs): the owner's text, a reset, the customer switch. */
export function useUpdateWidgetDocs() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; markdown?: string; resetToGenerated?: true; visibleToEndUsers?: boolean }) =>
      apiFetch<WidgetDocs>(`${API_BASE}/v1/widgets/${encodeURIComponent(id)}/docs`, put(body)),
    onSuccess: () => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" }),
  });
}

/** Regenerate the doc now (POST /api/v1/widgets/:id/generate-docs). */
export function useRegenerateWidgetDocs() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ widgetId: string; generating: boolean }>(`${API_BASE}/v1/widgets/${encodeURIComponent(id)}/generate-docs`, post({})),
    onSuccess: () => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreateWidget() {
  return useMutation({
    mutationFn: (input: CreateWidgetInput) =>
      apiFetch<Widget>(`${API_BASE}/v1/widgets`, post(input)),
    // Same reason as the update hook below: a create has to reach the
    // tenant-scoped preview list too.
    onSuccess: () =>
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" }),
  });
}

export function useUpdateWidget() {
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateWidgetInput & { id: string }) =>
      apiFetch<Widget>(`${API_BASE}/v1/widgets/${encodeURIComponent(id)}`, put(input)),
    onSuccess: () => {
      // Every widget query, not just this org's list. The "view as <tenant>" preview
      // caches under keys.widgets.list(tenantId) — a different key — so editing
      // an integration's customer visibility and publishing left the preview
      // rendering the state the change had just removed, and only F5 fixed it.
      // The admin was shown the opposite of what their own change did.
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" });
    },
  });
}

/**
 * Reloads a widget list held outside React Query. The embed keeps its catalog
 * in component state, so invalidating the "widgets" queries does not reach it;
 * it provides this so a version switch redraws the card there too.
 */
export const WidgetsRefreshContext = createContext<(() => void) | null>(null);

export interface ForkLineage {
  forkId: string;
  type: "workflow" | "trigger" | "config" | "connector" | "widget";
  sourceId: string;
  sourceOrgId: string;
  sourceOrgName: string | null;
  sourceName: string | null;
}

/** What each of the viewing org's copies was customised from. Only the org that made a copy gets an answer. */
export function useForkLineage(ids: string[], endOrgId?: string) {
  const { userContext } = useAuth();
  const orgId = endOrgId ?? userContext?.orgId;
  const list = [...new Set(ids)].sort().join(",");
  return useQuery({
    queryKey: keys.widgets.lineage(orgId, list),
    queryFn: () =>
      apiFetch<ForkLineage[]>(
        `${API_BASE}/v1/forks/lineage?ids=${encodeURIComponent(list)}${endOrgId ? `&endOrgId=${encodeURIComponent(endOrgId)}` : ""}`,
      ),
    enabled: !!orgId && list.length > 0,
    staleTime: 60_000,
  });
}

/** Choose which version of a customised integration the customer runs. */
export function useSetWidgetActiveVersion(endOrgId?: string) {
  const refreshOutsideQuery = useContext(WidgetsRefreshContext);
  return useMutation({
    mutationFn: ({ sourceWidgetId, activeWidgetId }: { sourceWidgetId: string; activeWidgetId: string }) =>
      apiFetch<{ sourceWidgetId: string; activeWidgetId: string; kind: "custom" | "original"; paused: string[]; resumed: string[]; carried: string[]; needsReconnect?: string[]; failed?: Array<{ installationId: string; error: string }> }>(
        `${API_BASE}/v1/widgets/${encodeURIComponent(sourceWidgetId)}/active-version${endOrgId ? `?endOrgId=${encodeURIComponent(endOrgId)}` : ""}`,
        put({ activeWidgetId }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" });
      refreshOutsideQuery?.();
    },
  });
}

/**
 * A delete whose widget is already gone has done what it was asked to. A second
 * confirm click on the same row, or a delete another tab already made, answers
 * 404 "Widget not found" -- and treating that as a failure is what surfaced as an
 * uncaught error on /widgets/:id (KAT-8735). Every other failure still throws.
 */
export async function deleteWidgetRequest(id: string): Promise<{ deleted: boolean; id: string }> {
  try {
    return await apiFetch<{ deleted: boolean; id: string }>(`${API_BASE}/v1/widgets/${encodeURIComponent(id)}`, del);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { deleted: true, id };
    throw err;
  }
}

export function useDeleteWidget() {
  return useMutation({
    mutationFn: deleteWidgetRequest,
    onSuccess: () =>
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" }),
  });
}
